'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import { VisualizationWrapper } from './visualization-wrapper';
import { useVisualizationContext } from './context';
import { extractFrontmatter, buildMermaidConfig } from '@/lib/mermaid-config';
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';

// Re-export for tests and other client consumers
export { extractFrontmatter, buildMermaidConfig };

interface MermaidRendererProps {
  code: string;
  title?: string;
  vizId?: string;
  renderActions?: React.ReactNode;
  /** When true, renders without VisualizationWrapper and fills parent container */
  screenshotMode?: boolean;
  /** Called when user clicks "Retry" on a render error, with error details + code */
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}

function initMermaid(frontmatterConfig?: Record<string, unknown> | null) {
  mermaid.initialize(buildMermaidConfig(frontmatterConfig));
}

/**
 * Normalize mermaid SVG to have explicit pixel dimensions.
 * Mermaid outputs width="100%" + style="max-width: Npx" which breaks
 * inside absolutely positioned containers. We replace with pixel values.
 */
export function normalizeSvg(svgHtml: string): { html: string; width: number; height: number } {
  const temp = document.createElement('div');
  temp.innerHTML = svgHtml;
  const svg = temp.querySelector('svg');

  if (!svg) return { html: svgHtml, width: 800, height: 600 };

  // Fix mermaid mindmap bug: mermaid applies cScaleLabel colors as `fill` on
  // SVG <text> elements but NOT as CSS `color` on <span>/<div> inside
  // <foreignObject>. Those HTML elements inherit color from the page body,
  // making text invisible on dark-background nodes.
  // We read mermaid's own generated fill colors and mirror them as CSS color rules.
  const styleEl = svg.querySelector('style');
  if (styleEl?.textContent) {
    const css = styleEl.textContent;
    let extraRules = '';
    // Match mermaid's section text fill rules (handles both `.section-N text` and `.section-N>text`)
    const fillRegex = /\.section-(\d+)\s*>?\s*text\s*\{[^}]*?fill:\s*([^;!}\s]+)/g;
    let m;
    while ((m = fillRegex.exec(css)) !== null) {
      const sectionNum = m[1];
      const fillColor = m[2];
      // Mirror the fill as color on HTML elements inside foreignObject
      extraRules += `.section-${sectionNum} foreignObject span,.section-${sectionNum} foreignObject div,.section-${sectionNum} foreignObject p{color:${fillColor} !important;}\n`;
    }
    if (extraRules) {
      styleEl.textContent = css + '\n' + extraRules;
    }
  }

  let w = 0;
  let h = 0;

  // Read viewBox for intrinsic dimensions
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map(Number);
    w = parts[2] || 0;
    h = parts[3] || 0;
  }

  // Fallback: parse max-width from style
  if (!w) {
    const maxW = svg.style.maxWidth;
    if (maxW) w = parseFloat(maxW) || 0;
  }

  // Fallback: width/height attributes (if not percentage)
  if (!w) {
    const wAttr = svg.getAttribute('width') || '';
    if (wAttr && !wAttr.includes('%')) w = parseFloat(wAttr) || 0;
  }
  if (!h) {
    const hAttr = svg.getAttribute('height') || '';
    if (hAttr && !hAttr.includes('%')) h = parseFloat(hAttr) || 0;
  }

  // Final fallback
  w = w || 800;
  h = h || 600;

  // Set explicit pixel dimensions but constrain to container
  svg.setAttribute('width', String(w));
  svg.setAttribute('height', String(h));
  svg.style.maxWidth = '100%';  // Constrain to container to prevent overflow
  svg.style.height = 'auto';  // Maintain aspect ratio

  return { html: temp.innerHTML, width: w, height: h };
}

export function MermaidRenderer({ code, title, vizId, renderActions, screenshotMode, onRetryError }: MermaidRendererProps) {
  const [svgContent, setSvgContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const svgDimsRef = useRef({ width: 800, height: 600 });

  useProductTelemetryOnce(
    'visualization_render_failed',
    Boolean(error),
    {
      tool_name: 'mermaid',
      failure_kind: 'runtime-render-error',
      visualization_id: vizId,
    },
    error ? `${vizId ?? 'mermaid'}:${error}` : undefined,
  );

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code) return;

      try {
        setError(null);

        // Extract YAML frontmatter (if any) and apply config via initialize()
        const { config, diagramCode } = extractFrontmatter(code);
        initMermaid(config);

        // CSS IDs can't start with numbers - always prefix with "mermaid-"
        const id = vizId ? `mermaid-${vizId}` : `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, diagramCode);

        const { html, width, height } = normalizeSvg(svg);
        svgDimsRef.current = { width, height };
        setSvgContent(html);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [code]);

  if (error) {
    return (
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium text-destructive">Failed to render diagram</p>
            {onRetryError && (
              <button
                onClick={() => onRetryError({ toolName: 'mermaid', error, code })}
                className="shrink-0 px-3 py-1 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Retry
              </button>
            )}
          </div>
          <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap break-words overflow-hidden">{error}</pre>
          <details className="mt-2">
            <summary className="text-xs text-muted-foreground cursor-pointer">Show code</summary>
            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">{code}</pre>
          </details>
        </div>
      </div>
    );
  }

  if (screenshotMode) {
    // For screenshots: scale to fit viewport while preserving aspect ratio
    // The screenshot API captures at a specific resolution (e.g., 1920x1080)
    // We need to scale large diagrams to fit within that viewport
    const { width: intrinsicWidth, height: intrinsicHeight } = svgDimsRef.current;

    // Calculate scale to fit within viewport (with small padding)
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth * 0.95 : 1920;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight * 0.95 : 1080;

    const scaleX = viewportWidth / intrinsicWidth;
    const scaleY = viewportHeight / intrinsicHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Never scale up, only down

    const scaledWidth = intrinsicWidth * scale;
    const scaledHeight = intrinsicHeight * scale;

    // Update SVG to use scaled dimensions
    const scaledSvg = svgContent
      .replace(/width="[^"]*"/, `width="${scaledWidth}"`)
      .replace(/height="[^"]*"/, `height="${scaledHeight}"`)
      .replace(/style="[^"]*"/, `style="max-width: 100%; height: auto;"`);

    return (
      <div
        style={{
          width: scaledWidth,
          height: scaledHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          colorScheme: 'light',
          color: 'CanvasText',
        }}
        dangerouslySetInnerHTML={{ __html: scaledSvg }}
      />
    );
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={450}
      exportData={{
        type: 'mermaid',
        content: code
      }}
      allowFitToggle={true}
      renderActions={renderActions}
      visualizationId={vizId}
    >
      <MermaidContent svgContent={svgContent} svgDimsRef={svgDimsRef} />
    </VisualizationWrapper>
  );
}

/** Inner component that reads visualization context for fullscreen detection */
function MermaidContent({
  svgContent,
  svgDimsRef,
}: {
  svgContent: string;
  svgDimsRef: React.RefObject<{ width: number; height: number }>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [zoomState, setZoomState] = useState({ x: 0, y: 0, k: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const { isFullscreen } = useVisualizationContext();

  const fitToScreen = useCallback(() => {
    if (!containerRef.current || !svgDimsRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    if (containerWidth === 0 || containerHeight === 0) return;

    const { width, height } = svgDimsRef.current;

    // Calculate scale to fit with padding
    const padding = 40;
    const scaleX = (containerWidth - padding) / width;
    const scaleY = (containerHeight - padding) / height;
    const scale = Math.min(scaleX, scaleY, 3);

    // Center
    const x = (containerWidth - width * scale) / 2;
    const y = (containerHeight - height * scale) / 2;

    setZoomState({ x, y, k: scale });
  }, [svgDimsRef]);

  // Fit to screen when SVG content changes or container resizes (e.g. fullscreen toggle)
  useEffect(() => {
    if (!svgContent || !containerRef.current) return;

    const container = containerRef.current;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]?.contentRect.height > 0) {
        fitToScreen();
      }
    });
    observer.observe(container);

    // Also try immediately after a frame for the initial render
    requestAnimationFrame(() => {
      if (container.clientHeight > 0) fitToScreen();
    });

    return () => observer.disconnect();
  }, [svgContent, fitToScreen, isFullscreen]);

  const handleWheel = (e: React.WheelEvent) => {
    if (!isFullscreen) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = -e.deltaY * 0.001;
      const newScale = Math.min(Math.max(0.1, zoomState.k + delta), 5);
      setZoomState(prev => ({ ...prev, k: newScale }));
    } else {
      e.stopPropagation();
      setZoomState(prev => ({
        ...prev,
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isFullscreen) return;
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isFullscreen || !isDragging) return;
    const dx = e.clientX - lastMousePos.x;
    const dy = e.clientY - lastMousePos.y;
    setZoomState(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div
      className={`w-full h-full relative overflow-hidden bg-white rounded-md ${isFullscreen ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ colorScheme: 'light', color: 'CanvasText' }}
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        ref={contentRef}
        className="absolute origin-top-left will-change-transform"
        style={{
          transform: `translate(${zoomState.x}px, ${zoomState.y}px) scale(${zoomState.k})`,
        }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />

      {/* Controls — only in fullscreen */}
      {isFullscreen && (
        <div className="absolute bottom-4 right-4 flex gap-1 bg-background/80 backdrop-blur border rounded-md shadow-sm p-1">
          <button
            onClick={() => setZoomState(s => ({ ...s, k: Math.min(s.k * 1.2, 5) }))}
            className="p-1.5 hover:bg-muted rounded text-sm font-medium w-8 h-8 flex items-center justify-center"
            title="Zoom In"
          >
            +
          </button>
          <button
            onClick={fitToScreen}
            className="p-1.5 hover:bg-muted rounded text-xs px-2 h-8 flex items-center justify-center"
            title="Fit to Screen"
          >
            Fit
          </button>
          <button
            onClick={() => setZoomState(s => ({ ...s, k: Math.max(s.k / 1.2, 0.1) }))}
            className="p-1.5 hover:bg-muted rounded text-sm font-medium w-8 h-8 flex items-center justify-center"
            title="Zoom Out"
          >
            -
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Renders mermaid output from a tool call
 */
export function MermaidToolOutput({ output, vizId, renderActions, onRetryError }: { output: { success: boolean; title?: string; code?: string; error?: string }; vizId?: string; renderActions?: React.ReactNode; onRetryError?: (info: { toolName: string; error: string; code: string }) => void }) {
  if (!output.success) {
    return (
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="p-4 border border-amber-500/50 rounded-lg bg-amber-500/10">
          <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Diagram Error</p>
          <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap break-words overflow-hidden">{output.error}</pre>
        </div>
      </div>
    );
  }

  if (!output.code) {
    return null;
  }

  return <MermaidRenderer code={output.code} title={output.title} vizId={vizId} renderActions={renderActions} onRetryError={onRetryError} />;
}
